import { isOwnHost } from "./cookieDomain";

/**
 * Query-string keys that identify a genuine campaign arrival. `gtf_campaign`
 * is deliberately excluded: it is a promo code that our own marketing site
 * appends to the web → app handoff, not an attribution signal.
 */
export const ATTRIBUTION_QUERY_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "utm_test",
  "tid",
  "gclid",
  "gbraid",
  "wbraid",
  "msclkid",
  "fbclid",
  "ucc",
];

/** Keys the marketing site may forward in the handoff URL for the landing context. */
export const HANDOFF_CONTEXT_KEYS = ["entryPage", "referrerUrl"] as const;

const MAX_URL_LENGTH = 2000;

export const referrerHostname = (referrer: string | undefined | null): string | null => {
  if (!referrer) return null;
  try {
    return new URL(referrer).hostname.toLowerCase();
  } catch {
    return null;
  }
};

/** True when the document referrer is one of our own hosts (life5.xx, app.life5.xx, ...). */
export const isOwnReferrer = (
  referrer: string | undefined | null,
  cookieDomain?: string
): boolean => {
  const host = referrerHostname(referrer);
  return host ? isOwnHost(host, cookieDomain) : false;
};

export const hasAttributionParams = (query: URLSearchParams): boolean =>
  ATTRIBUTION_QUERY_KEYS.some((key) => {
    const value = query.get(key);
    return value !== null && value !== "" && value !== "null";
  });

/**
 * A new external arrival is the only case in which the stored entry page and
 * referrer must be replaced: campaign parameters in the URL AND a referrer
 * that is not our own site. The web → app handoff (referrer life5.xx) must
 * never reset the landing captured on the marketing site.
 */
export const isNewExternalArrival = (
  query: URLSearchParams,
  referrer: string | undefined | null,
  cookieDomain?: string
): boolean => hasAttributionParams(query) && !isOwnReferrer(referrer, cookieDomain);

/**
 * Whether the app may seal its own URL as the entry page. It must not when the
 * visitor comes from our marketing site: the real landing lives there (or was
 * deleted by the CMP before consent) and writing the app URL would be
 * indistinguishable from a genuine direct landing on the app.
 */
export const shouldSealEntryPage = (
  existingEntryPage: string | null,
  referrer: string | undefined | null,
  cookieDomain?: string
): boolean => existingEntryPage === null && !isOwnReferrer(referrer, cookieDomain);

const cleanUrlParam = (value: string | null): string | null => {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed || trimmed === "null" || trimmed === "undefined") return null;
  if (!/^https?:\/\//i.test(trimmed)) return null;
  return trimmed.slice(0, MAX_URL_LENGTH);
};

/** Landing context forwarded by the marketing site in the handoff URL. */
export const readHandoffContext = (
  query: URLSearchParams
): { entryPage: string | null; referrerUrl: string | null } => ({
  entryPage: cleanUrlParam(query.get("entryPage")),
  referrerUrl: cleanUrlParam(query.get("referrerUrl")),
});
